import React from 'react';
import { AlertCircleIcon, HouseIcon, LockIcon, RulerIcon, SunIcon } from 'lucide-react';
import { AddressForm } from '../AddressForm';
import type { AddressQuery } from '../../types/house';
import type { LookupErrorKind } from '../../utils/bag';
interface AddressStartProps {
  errorKind: LookupErrorKind | null;
  onSubmit: (query: AddressQuery) => void;
  onDemo: () => void;
}
const checks = [{
  icon: HouseIcon,
  title: 'Je woning',
  text: 'Type, bouwjaar en woonoppervlak uit de BAG'
}, {
  icon: RulerIcon,
  title: 'Je dak',
  text: 'Oppervlak en ruimte voor zonnepanelen'
}, {
  icon: SunIcon,
  title: 'Je besparing',
  text: 'Welke batterij past en wanneer hij zich terugverdient'
}];
export function AddressStart({
  errorKind,
  onSubmit,
  onDemo
}: AddressStartProps) {
  return <div className="py-4 lg:py-8" data-id="element-375" data-mp="3ejv2w65ndnxz">
      <h1 className="max-w-xl text-4xl font-semibold leading-[1.02] tracking-tightest text-ink sm:text-5xl" data-id="element-376" data-mp="3eitp3tx8ynck">
        Ontdek in 2 minuten wat een thuisbatterij jouw huis oplevert.
      </h1>
      <p className="mt-5 max-w-lg text-lg leading-relaxed text-ink-soft" data-id="element-377" data-mp="1xr37zjte2mx">
        We zoeken je woning op de luchtfoto, meten je dak en rekenen je besparing door na het einde van de
        salderingsregeling.
      </p>

      {errorKind && <div role="alert" className="mt-8 flex gap-3 rounded-2xl border border-danger/30 bg-white p-4" data-id="element-378" data-mp="avkyr89r8s91">
          <AlertCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden="true" data-id="element-379" data-mp="fuo28oa36qew" />
          <div className="text-[15px]" data-id="element-380" data-mp="3ecba82o3vxi4">
            <p className="font-medium text-ink" data-id="element-381" data-mp="3jcxx3i5e4mtz">
              {errorKind === 'not-found' ? 'We konden dit adres niet vinden.' : 'De adresgegevens zijn nu niet bereikbaar.'}
            </p>
            <p className="mt-1 text-ink-muted" data-id="element-382" data-mp="3t30pbg4otwr9">
              {errorKind === 'not-found' ? 'Controleer je postcode en huisnummer en probeer het opnieuw.' : 'Probeer het zo nog eens, of bekijk de scan met een voorbeeldwoning.'}
            </p>
          </div>
        </div>}

      <div className="mt-8 rounded-3xl bg-white p-5 sm:p-7" data-id="element-383" data-mp="2sls4bjnkx1gq">
        <AddressForm onSubmit={onSubmit} idPrefix="scan" submitLabel="Start scan" data-id="element-384" data-mp="2sjxebqxgaj5s" />
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5" data-id="element-385" data-mp="2okgaf1xo9u2q">
          <p className="inline-flex items-center gap-2 text-sm text-ink-muted" data-id="element-386" data-mp="32e7h82k0qzur">
            <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-387" data-mp="2xdjrdk7j0p24" />
            We slaan je adres niet op zonder toestemming.
          </p>
          <button type="button" onClick={onDemo} className="text-[15px] font-medium text-ink underline underline-offset-4 hover:text-forest-light" data-id="element-388" data-mp="25lu9bn25o2yt">
            Bekijk een voorbeeldwoning
          </button>
        </div>
      </div>

      <h2 className="mt-12 text-sm font-medium text-ink-muted" data-id="element-389" data-mp="2eolmbraoj6sk">Wat we voor je uitzoeken</h2>
      <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-390" data-mp="3uyxr5yofm1yz">
        {checks.map(({
        icon: Icon,
        title,
        text
      }) => <li key={title} className="flex items-center gap-4 py-4" data-id="element-391" data-mp="1btwob62nz5a6">
            <Icon className="h-5 w-5 shrink-0 text-forest-light" aria-hidden="true" data-id="element-392" data-mp="1bslkx7d4rzjl" />
            <p className="text-[16px] text-ink" data-id="element-393" data-mp="1gukks79nwebs">
              <span className="font-medium" data-id="element-394" data-mp="12thypuio340v">{title}</span>
              <span className="text-ink-muted" data-id="element-395" data-mp="12v2qxdhieavk"> — {text}</span>
            </p>
          </li>)}
      </ul>
    </div>;
}